import {isFeatureEnabled} from 'feature-flags';
import {
  Alert,
  Button,
  FormField,
  openDialog,
  Separator,
  type FormControl,
} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import type {ComponentProps, ReactNode} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil, isNotNil, partition} from 'ramda';

import type {
  ConfigurationDetailResponseBody,
  ConfiguratorBundleResponseBody,
  EngineTechnicalData,
  EvaluateConfigurationResponseBody,
  PriceBasis,
  VariantTechnicalData,
} from '@dms/api/new-vehicle';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {
  ConfigurationCustomerView,
  BasisPrice,
  PriceBreakdown,
  SummaryRail,
  useNewVehicleCatalogueLabels,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {EntityCustomFields} from '@dms/shared';

import {EMPTY_PLACEHOLDER} from 'shared/web';

import {ColourSwatch} from '../../../../components/ColourSwatch/ColourSwatch';
import {
  formatBatteryCapacity,
  formatConsumptionAndEmission,
  formatWithUnit,
} from '../../../NewVehicleCatalogue/utils/formatConsumptionAndEmission';
import type {ConfiguratorFormValues} from '../../NewVehicleConfigurator';
import {getConfiguratorPriceState} from '../../utils/getConfiguratorPriceState';
import type {ReviewPriceSource} from '../../utils/getReviewPriceSource';
import {getVariantLabel} from '../../utils/getVariantLabel';
import {isStandardFeature} from '../../utils/isStandardFeature';
import {getEvaluationLineLabel} from '../../utils/newVehicleLabels';
import {StepIntro} from '../StepIntro';

export type ReviewProps = {
  basis?: PriceBasis;
  isBundleError?: boolean;
  control: FormControl<ConfiguratorFormValues>;
  evaluation?: EvaluateConfigurationResponseBody;
  detail?: ConfigurationDetailResponseBody;
  bundle?: ConfiguratorBundleResponseBody;
  currency?: string;
  context?: {makeName?: string; modelName?: string; modelYearName?: string};
  canViewCost: boolean;
  canEditCustomFields: boolean;
  canWrite: boolean;
  isPreview?: boolean;
  priceSource: ReviewPriceSource;
  /** Live evaluation settled; irrelevant once a saved snapshot exists. */
  isSettled: boolean;
  isRepricing?: boolean;
  onReprice?: VoidFunction;
  onStepChange?: (stepIndex: number) => void;
};

const t = (key: string, defaultValue: string) => i18n.t(key, {defaultValue});
const getPriceGuaranteeLabel = (priceValidUntil: string | null | undefined, isSaved: boolean) =>
  match({isSaved, priceValidUntil})
    .with({isSaved: false}, () =>
      t('entity.newVehicle.labels.pricesFreeze', 'Prices freeze when you save')
    )
    .with(
      {priceValidUntil: Pattern.string},
      ({priceValidUntil: date}) =>
        `${t('entity.newVehicle.labels.priceValidUntil', 'Price valid until')} ${date}`
    )
    .otherwise(() => t('entity.newVehicle.labels.noExpiry', 'No expiry'));
const STEP_TRIM = 0;
const STEP_OPTIONS = 2;
const STEP_LOOK = 3;

function ReviewSection(props: {
  title: string;
  onEdit?: VoidFunction;
  children: ReactNode;
  isHighlighted?: boolean;
}) {
  return (
    <Box
      backgroundColor={props.isHighlighted ? 'palettes.blue.10.100' : 'general.white'}
      border="1px solid"
      borderColor={props.isHighlighted ? 'palettes.blue.30.100' : 'general.separator'}
      borderRadius="large"
      padding={5}
    >
      <VStack spacing={4}>
        <HStack justify="space-between" align="center">
          <Heading size={4}>{props.title}</Heading>
          <Show when={props.onEdit}>
            <Button
              variant="link"
              size="small"
              title={t('general.actions.edit', 'Edit')}
              rightIcon="action/open_in_new"
              onClick={props.onEdit}
            />
          </Show>
        </HStack>
        {props.children}
      </VStack>
    </Box>
  );
}

function Row(props: {label: string; value?: ReactNode | string; leading?: ReactNode}) {
  const isEmpty = props.value === undefined || props.value === null || props.value === '';
  return (
    <HStack spacing={4} align="flex-start">
      <Box width={40} flexShrink={0}>
        <Text size="small" color="secondary">
          {props.label}
        </Text>
      </Box>
      <HStack spacing={2} align="center">
        <Show when={props.leading}>{props.leading}</Show>
        {typeof props.value === 'string' || isEmpty ? (
          <Text size="small" alternative color={isEmpty ? 'tertiary' : 'primary'}>
            {isEmpty ? EMPTY_PLACEHOLDER : props.value}
          </Text>
        ) : (
          props.value
        )}
      </HStack>
    </HStack>
  );
}

function TechnicalDataRows(props: {
  engine: EngineTechnicalData | undefined;
  variant: VariantTechnicalData | undefined;
}) {
  const labels = useNewVehicleCatalogueLabels();
  const batteryCapacity = formatBatteryCapacity(props.engine);
  const consumptionAndEmission = formatConsumptionAndEmission(props.variant);
  const emissionClass = props.variant?.emissionClass;
  const electricRange = props.variant?.electricRange;
  const combinedRange = props.variant?.combinedRange;
  return (
    <>
      <Show when={isNotNil(batteryCapacity)}>
        <Row label={i18n.t('entity.vehicle.labels.batteryCapacity')} value={batteryCapacity} />
      </Show>
      <Show when={isNotNil(electricRange)}>
        <Row
          label={i18n.t('entity.vehicle.labels.batteryElectricRange')}
          value={formatWithUnit(electricRange, 'general.metric.km')}
        />
      </Show>
      <Show when={isNotNil(combinedRange)}>
        <Row
          label={i18n.t('entity.vehicle.labels.combinedRange')}
          value={formatWithUnit(combinedRange, 'general.metric.km')}
        />
      </Show>
      <Show when={consumptionAndEmission !== ''}>
        <Row
          label={i18n.t('entity.vehicle.labels.consumptionAndEmissionSectionTitle')}
          value={consumptionAndEmission}
        />
      </Show>
      <Show when={isNotNil(emissionClass)}>
        <Row
          label={i18n.t('entity.vehicle.labels.emissionClass')}
          value={labels.getEmissionClass(emissionClass)}
        />
      </Show>
    </>
  );
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function Review(props: ReviewProps) {
  const trimId = useWatch({control: props.control, name: 'trimId'});
  const variantId = useWatch({control: props.control, name: 'variantId'});
  const colourId = useWatch({control: props.control, name: 'colourId'});
  const upholsteryId = useWatch({control: props.control, name: 'upholsteryId'});
  const featureIds = useWatch({control: props.control, name: 'featureIds'}) ?? [];
  const packageIds = useWatch({control: props.control, name: 'packageIds'}) ?? [];
  const trim = props.bundle?.trims.find(({id}) => id === trimId);
  const variant = props.bundle?.variants.find(({id}) => id === variantId);
  const engine = props.bundle?.engines.find(({id}) => id === variant?.engineId);
  const colour = props.bundle?.colours.find(({id}) => id === colourId);
  const upholstery = props.bundle?.upholsteries.find(({id}) => id === upholsteryId);
  const selectedPackages = (props.bundle?.packages ?? []).filter(({id}) => packageIds.includes(id));
  // STANDARD equipment is included by the trim, never an option (D72).
  const [standardFeatures, optionalFeatures] = partition(
    (feature) => isStandardFeature(feature, trimId),
    props.bundle?.features ?? []
  );
  const standardIds = new Set(standardFeatures.map(({id}) => id));
  const optionFeatureIds = featureIds.filter((id) => !standardIds.has(id));
  const selectedFeatures = optionalFeatures.filter(({id}) => featureIds.includes(id));
  // An upgrade wheel, picked or brought by a package or rule, replaces the standard wheel (D70).
  const buildIds = new Set([
    ...optionFeatureIds,
    ...selectedPackages.flatMap(({memberFeatureIds}) => memberFeatureIds),
    ...(props.evaluation?.autoAddedSelectionIds ?? []),
  ]);
  const hasUpgradeWheel = optionalFeatures.some(
    (feature) => feature.kind === 'wheel' && buildIds.has(feature.id)
  );
  const includedFeatures = standardFeatures.filter(
    (feature) => !(hasUpgradeWheel && feature.kind === 'wheel')
  );
  const snapshot = props.priceSource === 'saved' ? props.detail?.currentPriceSnapshot : undefined;
  const variantLabel = getVariantLabel(variantId, props.bundle);
  // Old snapshots froze a trim-only variant label, so the saved Review names the vehicle from the
  // catalogue and keeps the snapshot label only when the catalogue cannot resolve the variant.
  const isSavedVariantFallback = isNotNil(snapshot) && isNil(variantLabel);
  const savedVariant = snapshot?.lines.find(({type}) => type === 'variant');
  const savedColour = snapshot?.lines.find(({type}) => type === 'colour');
  const savedUpholstery = snapshot?.lines.find(({type}) => type === 'upholstery');
  const savedPackages = snapshot?.lines.filter(({type}) => type === 'package') ?? [];
  const savedFeatures = snapshot?.lines.filter(({type}) => type === 'feature') ?? [];
  const displayedPackages = snapshot
    ? savedPackages.map(({id, label}) => ({id, label}))
    : selectedPackages.map(({id, name}) => ({id, label: name}));
  const displayedFeatures = snapshot
    ? savedFeatures.map(({id, label}) => ({id, label}))
    : selectedFeatures.map(({id, name}) => ({id, label: name}));
  const totals = snapshot
    ? {net: snapshot.totalNet, vat: snapshot.vat, gross: snapshot.totalGross}
    : props.evaluation?.totals;
  const currency = snapshot?.currency ?? props.bundle?.currency ?? props.currency ?? '';
  const vatRateLabel = i18n.t('entity.newVehicle.labels.vatRate', {
    defaultValue: 'VAT {rate} %',
    rate: snapshot?.vatRate ?? props.bundle?.vatRate ?? '',
  });
  const proposed = snapshot
    ? {
        net: snapshot.proposedSellingPriceNet ?? undefined,
        gross: snapshot.proposedSellingPriceGross ?? undefined,
      }
    : {
        net: props.evaluation?.proposedSellingPriceNet ?? undefined,
        gross: props.evaluation?.proposedSellingPriceGross ?? undefined,
      };
  const cost = props.canViewCost
    ? {
        totalCostNet: snapshot
          ? (snapshot.totalCostNet ?? undefined)
          : (props.evaluation?.totals.costNet ?? undefined),
      }
    : undefined;
  const lines =
    snapshot?.lines.map((line) => ({
      id: `${line.type}-${line.id}`,
      label: line.type === 'variant' ? (variantLabel ?? line.label) : line.label,
      amountNet: line.unitNet,
      amountGross: line.unitGross,
    })) ??
    props.evaluation?.items
      .filter((item) => item.priceNet !== null)
      .map((item) => ({
        id: `${item.type}-${item.id}`,
        label: props.bundle
          ? getEvaluationLineLabel(item, props.bundle, t)
          : t('entity.newVehicle.labels.unknownItem', 'Unknown item'),
        amountNet: item.priceNet ?? '0.00',
        amountGross: item.priceGross ?? '0.00',
      }));
  const priceState = getConfiguratorPriceState(props.basis, props.isBundleError ?? false);
  const priceSlice = match(priceState)
    .returnType<
      Omit<ComponentProps<typeof PriceBreakdown>, 'currency'> & {
        headline?: Pick<ComponentProps<typeof BasisPrice>, 'net' | 'gross' | 'basis'>;
      }
    >()
    .with({kind: 'loading'}, () => ({isPending: true}))
    .with({kind: 'known'}, {kind: 'unavailable'}, (state) => {
      const basis = state.kind === 'known' ? state.basis : state.displayBasis;
      return {
        basis,
        lines,
        totals,
        proposed,
        cost,
        isPending: isNil(snapshot) && !props.isSettled,
        headline: totals ? {net: totals.net, gross: totals.gross, basis} : undefined,
      };
    })
    .exhaustive();
  const {headline, lines: priceLines, ...price} = priceSlice;
  const linkedContext = [
    props.detail?.configuration.businessCaseId
      ? t('entity.newVehicle.labels.businessCase', 'Business case')
      : undefined,
    props.detail?.configuration.interestId
      ? t('entity.interest.labels.interest', 'Interest')
      : undefined,
  ].filter(Boolean);

  return (
    <HStack spacing={5} align="flex-start">
      <Box flex={1} minWidth={0}>
        <VStack spacing={5}>
          <Box paddingHorizontal={1}>
            <StepIntro
              title={t('entity.newVehicle.steps.reviewTitle', 'Step 5 — Review & save')}
              description={t(
                'entity.newVehicle.steps.reviewIntro',
                'Confirm the configuration. Saving locks in these prices — later catalogue price changes will not affect this vehicle.'
              )}
            />
          </Box>
          <VStack spacing={4}>
            <Show
              when={
                props.detail?.priceValidity === 'expired' || props.detail?.catalogueChanged === true
              }
            >
              <Alert
                layout="detailed"
                variant="warning"
                message={
                  props.detail?.catalogueChanged
                    ? t(
                        'entity.newVehicle.notifications.catalogueChanged',
                        'The catalogue changed after this price was saved'
                      )
                    : t(
                        'entity.newVehicle.notifications.priceExpired',
                        'The saved price has expired'
                      )
                }
                actions={
                  props.onReprice
                    ? [
                        {
                          title: t('entity.newVehicle.actions.reprice', 'Re-price'),
                          isDisabled: props.isRepricing,
                          isLoading: props.isRepricing,
                          onClick: props.onReprice,
                        },
                      ]
                    : []
                }
              />
            </Show>
            <Show when={!props.isPreview}>
              <ReviewSection title={t('entity.newVehicle.labels.configuration', 'Configuration')}>
                <Box maxWidth={160}>
                  <FormField
                    control={props.control}
                    type="text"
                    name="label"
                    label={t('entity.newVehicle.labels.configurationLabel', 'Configuration label')}
                    isRequired
                    isDisabled={!props.canWrite}
                    helperText={t(
                      'entity.newVehicle.labels.configurationLabelHelp',
                      'Shown in the configurations list and on the customer offer.'
                    )}
                  />
                </Box>
              </ReviewSection>
            </Show>
            <Show when={props.detail && !props.isPreview}>
              <Box
                backgroundColor="palettes.blue.10.100"
                border="1px solid"
                borderColor="palettes.blue.30.100"
                borderRadius="medium"
                padding={4}
              >
                <HStack justify="space-between" align="center" spacing={4}>
                  <HStack spacing={3} align="center">
                    <Icon value="action/visibility" size={5} color="palettes.blue.60.100" />
                    <VStack spacing={0}>
                      <Text size="small" alternative>
                        {t(
                          'entity.newVehicle.labels.customerViewTitle',
                          'This is what your customer sees'
                        )}
                      </Text>
                      <Text size="xSmall" color="secondary">
                        {t(
                          'entity.newVehicle.labels.customerViewIntro',
                          'Review the saved configuration and customer-safe prices.'
                        )}
                      </Text>
                    </VStack>
                  </HStack>
                  <Button
                    title={t('entity.newVehicle.actions.viewAsCustomer', 'View as customer')}
                    rightIcon="navigation/arrow_forward"
                    onClick={() =>
                      openDialog(
                        <ConfigurationCustomerView
                          configurationId={props.detail?.configuration.id ?? ''}
                        />,
                        {
                          size: 'large',
                          title: t('entity.newVehicle.labels.customerView', 'Customer view'),
                        }
                      )
                    }
                  />
                </HStack>
              </Box>
            </Show>
            <ReviewSection
              title={t('entity.newVehicle.labels.vehicle', 'Vehicle')}
              onEdit={props.onStepChange ? () => props.onStepChange?.(STEP_TRIM) : undefined}
            >
              {isSavedVariantFallback ? (
                <Row label={t('general.labels.variant', 'Variant')} value={savedVariant?.label} />
              ) : (
                <VStack spacing={2}>
                  <Row
                    label={t('entity.newVehicle.labels.makeModel', 'Make · Model')}
                    value={
                      `${props.context?.makeName ?? ''} ${props.context?.modelName ?? ''}`.trim() ||
                      undefined
                    }
                  />
                  <Row
                    label={t('entity.newVehicle.labels.modelYear', 'Model year')}
                    value={props.context?.modelYearName}
                  />
                  <Row
                    label={t('entity.newVehicle.labels.trimLevel', 'Trim level')}
                    value={trim?.name}
                  />
                  <Row
                    label={t('entity.newVehicle.labels.engineDrive', 'Engine & drive')}
                    value={engine ? `${engine.name} · ${engine.powerKw} kW` : undefined}
                  />
                  <TechnicalDataRows
                    engine={engine?.technicalData}
                    variant={variant?.technicalData}
                  />
                </VStack>
              )}
            </ReviewSection>
            <ReviewSection
              title={t('entity.newVehicle.labels.coloursUpholstery', 'Colours & upholstery')}
              onEdit={props.onStepChange ? () => props.onStepChange?.(STEP_LOOK) : undefined}
            >
              <VStack spacing={2}>
                <Row
                  label={t('entity.newVehicle.labels.colour', 'Colour')}
                  value={snapshot ? savedColour?.label : colour?.name}
                  leading={
                    !snapshot && colour ? (
                      <ColourSwatch name={colour.name} hex={colour.hex} size="dot" />
                    ) : undefined
                  }
                />
                <Row
                  label={t('entity.newVehicle.labels.upholstery', 'Upholstery')}
                  value={snapshot ? savedUpholstery?.label : upholstery?.name}
                  leading={
                    !snapshot && upholstery ? (
                      <ColourSwatch
                        name={upholstery.name}
                        hex={upholstery.interiorColorHex}
                        size="dot"
                      />
                    ) : undefined
                  }
                />
              </VStack>
            </ReviewSection>
            <ReviewSection
              title={t('entity.newVehicle.steps.optionsPackages', 'Options & packages')}
              onEdit={props.onStepChange ? () => props.onStepChange?.(STEP_OPTIONS) : undefined}
            >
              {displayedPackages.length + displayedFeatures.length === 0 ? (
                <Text size="small" color="tertiary">
                  {t('entity.newVehicle.labels.noneSelected', 'None selected')}
                </Text>
              ) : (
                <VStack spacing={2}>
                  {displayedPackages.map((item) => (
                    <Row
                      key={item.id}
                      label={t('entity.newVehicle.labels.package', 'Package')}
                      value={item.label}
                    />
                  ))}
                  {displayedFeatures.map((item) => (
                    <Row
                      key={item.id}
                      label={t('entity.newVehicle.labels.option', 'Option')}
                      value={item.label}
                    />
                  ))}
                </VStack>
              )}
              <Show when={includedFeatures.length > 0}>
                <Row
                  label={t('entity.newVehicle.labels.included', 'Included')}
                  value={includedFeatures.map(({name}) => name).join(', ')}
                />
              </Show>
            </ReviewSection>
            <ReviewSection title={t('entity.newVehicle.labels.pricing', 'Pricing')} isHighlighted>
              <VStack spacing={3}>
                <HStack justify="space-between" align="flex-end">
                  <Text size="small" color="secondary">
                    {snapshot
                      ? t('entity.newVehicle.labels.savedPriceSnapshot', 'Saved price snapshot')
                      : t('entity.newVehicle.labels.asConfigured', 'As configured')}
                  </Text>
                  <Show when={headline}>
                    {(amounts) => (
                      <BasisPrice
                        {...amounts}
                        currency={currency}
                        headingSize={2}
                        color="link"
                        noWrap
                        align="right"
                      />
                    )}
                  </Show>
                </HStack>
                <Row
                  label={t('entity.newVehicle.labels.priceGuarantee', 'Price guarantee')}
                  value={getPriceGuaranteeLabel(snapshot?.priceValidUntil, Boolean(snapshot))}
                />
                <Show when={linkedContext.length > 0}>
                  <Row
                    label={t('entity.newVehicle.labels.linkedTo', 'Linked to')}
                    value={linkedContext.join(' · ')}
                  />
                </Show>
                <Separator spacing={0} />
                <PriceBreakdown
                  {...price}
                  currency={currency}
                  lines={priceLines}
                  canViewCost={props.canViewCost}
                  vatRateLabel={vatRateLabel}
                />
              </VStack>
            </ReviewSection>
            <Show
              when={
                props.detail &&
                !props.isPreview &&
                isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE)
              }
            >
              <Box data-testid="new-vehicle-configuration-custom-fields-mount">
                <EntityCustomFields
                  resourceId={EntityResourceIds.newVehicleConfiguration}
                  recordId={props.detail?.configuration.id ?? ''}
                  isEditable={props.canEditCustomFields}
                  data-testid="new-vehicle-configuration-custom-fields"
                />
              </Box>
            </Show>
          </VStack>
        </VStack>
      </Box>
      <Box width={[72, 72, 76, 84]} flexShrink={0}>
        <SummaryRail
          rows={[
            {
              label: isSavedVariantFallback
                ? t('general.labels.variant', 'Variant')
                : t('entity.newVehicle.labels.trimLevel', 'Trim level'),
              value: isSavedVariantFallback ? savedVariant?.label : trim?.name,
            },
            {
              label: t('entity.newVehicle.labels.engineDrive', 'Engine & drive'),
              value:
                engine && !isSavedVariantFallback
                  ? `${engine.powerKw} kW · ${engine.name}`
                  : undefined,
            },
            {
              label: t('entity.newVehicle.labels.colour', 'Colour'),
              value: snapshot ? savedColour?.label : colour?.name,
            },
            {
              label: t('entity.newVehicle.labels.upholstery', 'Upholstery'),
              value: snapshot ? savedUpholstery?.label : upholstery?.name,
            },
            {
              label: t('entity.newVehicle.labels.optionsPackagesCount', 'Options · packages'),
              value: snapshot
                ? `${savedFeatures.length} · ${savedPackages.length}`
                : `${optionFeatureIds.length} · ${packageIds.length}`,
            },
          ]}
          price={{
            ...price,
            currency,
            canViewCost: props.canViewCost,
            vatRateLabel,
          }}
        />
      </Box>
    </HStack>
  );
}
